import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import "../css/auth.css";

const AuthPage = () => {
  const [mode, setMode] = useState('login'); // login | register | forgotPassword
  const [loginMethod, setLoginMethod] = useState('email'); // email | phone
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    otp: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [otpRequested, setOtpRequested] = useState(false);
  const { login, register, requestOtp, googleLogin, forgotPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/';

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (error) setError('');
    if (successMessage) setSuccessMessage('');
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const data = await login({ email: form.email, password: form.password }, 'email');
      if (data.isAdmin) {
        navigate('/admin', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await register({
        name: form.name,
        email: form.email,
        password: form.password,
        phone: form.phone
      });
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed');
    }
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await requestOtp(form.phone);
      setOtpRequested(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to request OTP');
    }
  };

  const handlePhoneLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const data = await login({ phone: form.phone, otp: form.otp }, 'phone');
      if (data.isAdmin) {
        navigate('/admin', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid OTP');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      const data = await forgotPassword(form.email);
      setSuccessMessage(data.message || 'Password reset link sent to your email');
      // In dev mode, show the token for testing
      if (data.devToken) {
        setSuccessMessage(`${data.message}. Dev token: ${data.devToken}`);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Load Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    script.onload = () => {
      // Initialize Google Sign-In
      window.google?.accounts?.id?.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: handleGoogleCredentialResponse
      });
    };

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleGoogleCredentialResponse = async (response) => {
    try {
      const data = await googleLogin(response.credential);
      if (data.isAdmin) {
        navigate('/admin', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError('Google login failed. Please try again.');
    }
  };

  const handleGoogleLogin = () => {
    if (window.google?.accounts?.id) {
      window.google.accounts.id.prompt();
    } else {
      setError('Google Sign-In is not loaded. Please refresh the page.');
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-tabs">
          <button
            className={mode === 'login' ? 'active' : ''}
            onClick={() => { setMode('login'); setError(''); setSuccessMessage(''); }}
          >
            Login
          </button>
          <button
            className={mode === 'register' ? 'active' : ''}
            onClick={() => { setMode('register'); setError(''); setSuccessMessage(''); }}
          >
            Register
          </button>
        </div>

        {mode === 'forgotPassword' ? (
          <div className="auth-form">
            <h3 className="auth-title">Forgot Password?</h3>
            <p className="auth-subtitle">Enter your email to receive a password reset link</p>
            
            <form onSubmit={handleForgotPassword}>
              <div className="form-row">
                <label>Email</label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  required
                />
              </div>
              
              {error && <div className="error-message">{error}</div>}
              {successMessage && <div className="success-message">{successMessage}</div>}
              
              <button className="btn-auth wide" type="submit" disabled={loading}>
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
            
            <div className="auth-back-link">
              <button onClick={() => { setMode('login'); setError(''); setSuccessMessage(''); }}>
                Back to Login
              </button>
            </div>
          </div>
        ) : mode === 'login' ? (
          <>
            {loginMethod === 'email' && (
              <form onSubmit={handleEmailLogin} className="auth-form">
                <div className="form-row">
                  <label>Email</label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-row">
                  <label>Password</label>
                  <input
                    name="password"
                    type="password"
                    value={form.password}
                    onChange={handleChange}
                    required
                  />
                </div>
                {error && <div className="error-message">{error}</div>}
                <button className="btn-auth wide" type="submit">
                  Login
                </button>
                
                {/* <div className="forgot-password-link">
                  <button type="button" onClick={() => { setMode('forgotPassword'); setError(''); setSuccessMessage(''); }}>
                    Forgot Password?
                  </button>
                </div> */}
              </form>
            )}

            {loginMethod === 'phone' && (
              <div className="auth-form">
                <div className="form-row">
                  <label>Phone</label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                  />
                </div>
                {!otpRequested ? (
                  <>
                    {error && <div className="error-message">{error}</div>}
                    <button
                      className="btn-auth wide"
                      onClick={handleRequestOtp}
                    >
                      Request OTP
                    </button>
                  </>
                ) : (
                  <>
                    <div className="form-row">
                      <label>OTP</label>
                      <input
                        name="otp"
                        value={form.otp}
                        onChange={handleChange}
                      />
                    </div>
                    {error && <div className="error-message">{error}</div>}
                    <button
                      className="btn-auth wide"
                      onClick={handlePhoneLogin}
                    >
                      Verify & Login
                    </button>
                  </>
                )}
              </div>
            )}

            <div className="auth-divider">OR</div>
            <button
              className="btn-google"
              type="button"
              onClick={handleGoogleLogin}
            >
              Continue with Google
            </button>
          </>
        ) : (
          <form onSubmit={handleRegister} className="auth-form">
            <div className="form-row">
              <label>Name</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-row">
              <label>Email</label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-row">
              <label>Phone</label>
              <input
                name="phone"
                value={form.phone}
                onChange={handleChange}
              />
            </div>
            <div className="form-row">
              <label>Password</label>
              <input
                name="password"
                type="password"
                value={form.password}
                onChange={handleChange}
                required
              />
            </div>
            <button className="btn-auth wide" type="submit">
              Create Account
            </button>
            {error && <div className="error-message">{error}</div>}
          </form>
        )}
      </div>
    </div>
  );
};

export default AuthPage;
